import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslateModule } from '@ngx-translate/core';
import { AccountsService } from '@modules/accounts/accounts.service';
import { Account } from '@modules/accounts/accounts.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { ProfileService } from 'app/core/profile/profile.service';
import { AccountWithSubAccounts, ProfileWithNames } from 'app/core/profile/profile.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { environment } from 'environments/environment';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'classy-layout',
  templateUrl: './classy.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    LanguagesComponent,
    FuseFullscreenComponent,
    RouterOutlet,
    MatSelectModule,
    MatOptionModule,
    MatFormFieldModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy {
  private readonly _router = inject(Router);
  private readonly _navigationService = inject(NavigationService);
  private readonly _userService = inject(UserService);
  private readonly _fuseMediaWatcherService = inject(FuseMediaWatcherService);
  private readonly _fuseNavigationService = inject(FuseNavigationService);
  private readonly _accountsService = inject(AccountsService);
  private readonly _profileService = inject(ProfileService);
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  isScreenSmall: boolean;
  navigation: Navigation;
  user: User;
  logo = environment.logopath;
  accounts$: Observable<Account[]>;
  accountControl: FormControl = new FormControl('');

  // Profile Switcher
  profilesGrouped: AccountWithSubAccounts[] = [];
  isLoadingProfiles = false;
  activeProfileId: string | null = null;
  activeSubAccountName: string | null = null;

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // Subscribe to navigation data
    this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
      this.navigation = navigation;
    });

    this.navigation = {
      compact: [],
      default: [],
      futuristic: [],
      horizontal: [],
    };
    this._navigationService.get().pipe(takeUntil(this._unsubscribeAll)).subscribe();

    // Subscribe to the user service
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.user = user;
    });

    // Subscribe to media changes
    this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
      // Check if the screen is small
      this.isScreenSmall = !matchingAliases.includes('md');
    });

    this.accounts$ = this._accountsService.getAccounts();

    this.loadSelectedAccount();

    this.accountControl.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
      this.saveSelectedAccount(value);
    });

    this._profileService.profilesGrouped$.pipe(takeUntil(this._unsubscribeAll)).subscribe((grouped) => {
      this.profilesGrouped = grouped;
    });

    this._profileService.activeProfileId$.pipe(takeUntil(this._unsubscribeAll)).subscribe((profileId) => {
      this.activeProfileId = profileId;
    });

    this._profileService.isLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loading) => {
      this.isLoadingProfiles = loading;
    });

    this._profileService.activeSubAccountName$.pipe(takeUntil(this._unsubscribeAll)).subscribe((name) => {
      this.activeSubAccountName = name;
    });

    this._profileService.loadProfilesGrouped().pipe(takeUntil(this._unsubscribeAll)).subscribe();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }

  /**
   * Save the selected account to localStorage
   */
  saveSelectedAccount(accountUID: string): void {
    localStorage.setItem('selectedAccountUID', accountUID);

    const selectedAccountName = this.getAccountNameById(accountUID);
    if (selectedAccountName) {
      localStorage.setItem('selectedAccountName', selectedAccountName);
    }

    window.dispatchEvent(new CustomEvent('accountChanged', { detail: { accountUID } }));
  }

  /**
   * Load the selected account from localStorage
   */
  loadSelectedAccount(): void {
    const selectedAccountUID = localStorage.getItem('selectedAccountUID');
    if (selectedAccountUID) {
      this.accountControl.setValue(selectedAccountUID);
    }
  }

  /**
   * Get account name by ID
   */
  getAccountNameById(accountUID: string): string | null {
    let accountName: string | null = null;
    this.accounts$.subscribe((accounts) => {
      const account = accounts.find(acc => acc._id === accountUID);
      if (account) {
        accountName = account.displayName;
      }
    });
    return accountName;
  }

  /**
   * Get user initials from name
   */
  getUserInitials(): string {
    if (!this.user?.name) {
      return '?';
    }
    const names = this.user.name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    }
    return names[0].charAt(0).toUpperCase();
  }

  trackByAccountGroup(index: number, group: AccountWithSubAccounts): string {
    return group.account.uuid;
  }

  trackByProfile(index: number, profile: ProfileWithNames): string {
    return profile.profileId;
  }

  onProfileMenuOpened(): void {
    if (this.profilesGrouped.length === 0) {
      this._profileService.loadProfilesGrouped().pipe(takeUntil(this._unsubscribeAll)).subscribe();
    }
  }

  selectProfile(profile: ProfileWithNames, event: Event): void {
    event.stopPropagation();
    if (profile.isActive) return;

    this._profileService
      .switchProfile(profile.profileId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          window.location.reload();
        },
        error: (err) => {
          console.error('Error switching profile:', err);
        },
      });
  }

  signOut(): void {
    this._router.navigate(['/sign-out']);
  }

  changePassword(): void {
    this._router.navigate(['/change-password']);
  }
}
